/**
 * QA Panel — PopupContainer (desktop).
 *
 * Wraps window.open verbatim — this is the original bridge behavior extracted
 * unchanged so desktop output stays byte-for-byte identical:
 *   - open()  → window.open('about:blank', ...) + document.write(html)
 *   - target  → the popup Window
 *   - close() → popup.close()
 *   - external-close poll → polls popup.closed every 1s (window chrome can
 *     close the popup out from under us)
 *
 * Must be invoked from a user gesture or the popup blocker intervenes; the
 * bridge surfaces a floating fallback button when open() returns false.
 */

import { POPUP_FEATURES, POPUP_WINDOW_NAME } from '../constants';
import type { PanelContainer } from './container';

export class PopupContainer implements PanelContainer {
    private popup: Window | null = null;
    private heartbeat: number | null = null;

    open(html: string): boolean {
        const popup = window.open('about:blank', POPUP_WINDOW_NAME, POPUP_FEATURES);
        if (!popup) {
            return false;
        }
        popup.document.open();
        popup.document.write(html);
        popup.document.close();
        this.popup = popup;
        return true;
    }

    target(): Window | null {
        if (!this.popup || this.popup.closed) return null;
        return this.popup;
    }

    isOpen(): boolean {
        return this.popup !== null && !this.popup.closed;
    }

    close(): void {
        this.stopHeartbeat();
        if (this.popup && !this.popup.closed) {
            this.popup.close();
        }
        this.popup = null;
    }

    focus(): void {
        if (this.popup && !this.popup.closed) {
            this.popup.focus();
        }
    }

    onExternalClose(callback: () => void): () => void {
        this.stopHeartbeat();
        this.heartbeat = window.setInterval(() => {
            if (!this.popup || this.popup.closed) {
                this.stopHeartbeat();
                this.popup = null;
                callback();
            }
        }, 1000);
        return () => this.stopHeartbeat();
    }

    private stopHeartbeat(): void {
        if (this.heartbeat !== null) {
            window.clearInterval(this.heartbeat);
            this.heartbeat = null;
        }
    }
}
